// src/services/apiService.js
// Centralized API service with environment-based configuration
// ============================================================
// CLIENT ID: All functions now REQUIRE clientId to be passed by callers
// No hardcoded defaults - callers must provide session.client_id
// All client_id values are strings for BigQuery compatibility
// ============================================================

// Get API base URL from environment variable or use default
const BACKEND_URL = import.meta.env.VITE_API_URL || import.meta.env.VITE_BACKEND_URL || 'http://localhost:8080';
const API_BASE = BACKEND_URL;

// Health check state management
let healthCheckRetries = 0;
const MAX_RETRIES = 5;
const RETRY_DELAYS = [1000, 2000, 4000, 8000, 16000];

/**
 * Validate and normalize clientId
 * @param {string|number|null} clientId - The client ID to validate
 * @param {string} functionName - Name of calling function for error messages
 * @returns {string} - Validated string clientId
 * @throws {Error} - If clientId is missing or invalid
 */
const validateClientId = (clientId, functionName) => {
  if (clientId === null || clientId === undefined || clientId === '') {
    console.error(`[apiService.${functionName}] clientId is required but was: ${clientId}`);
    throw new Error(`clientId is required for ${functionName}`);
  }
  return String(clientId);
};

/**
 * Generic fetch wrapper with error handling
 */
const fetchWithErrorHandling = async (path, options = {}) => {
  const url = `${API_BASE}${path}`;
  
  const defaultOptions = {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    credentials: 'include',
    ...options,
  };

  try {
    const response = await fetch(url, defaultOptions);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response;
  } catch (error) {
    console.error(`API request failed: ${url}`, error);
    throw error;
  }
};

/**
 * GET request wrapper
 */
export const get = async (path, options = {}) => {
  try {
    const response = await fetchWithErrorHandling(path, {
      ...options,
      method: 'GET',
    });
    return await response.json();
  } catch (error) {
    console.error(`GET request failed for ${path}:`, error);
    throw error;
  }
};

/**
 * POST request wrapper
 */
export const post = async (path, data = {}, options = {}) => {
  try {
    const response = await fetchWithErrorHandling(path, {
      ...options,
      method: 'POST',
      body: JSON.stringify(data),
    });
    return await response.json();
  } catch (error) {
    console.error(`POST request failed for ${path}:`, error);
    throw error;
  }
};

/**
 * PUT request wrapper
 */
export const put = async (path, data = {}, options = {}) => {
  try {
    const response = await fetchWithErrorHandling(path, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return await response.json();
  } catch (error) {
    console.error(`PUT request failed for ${path}:`, error);
    throw error;
  }
};

/**
 * Health check with exponential backoff retry
 */
export const checkHealth = async () => {
  try {
    const response = await get('/healthz');
    healthCheckRetries = 0;
    return {
      ok: response.ok || response.success || true,
      timestamp: Date.now(),
      retries: 0,
    };
  } catch (error) {
    if (healthCheckRetries < MAX_RETRIES) {
      const delay = RETRY_DELAYS[healthCheckRetries] || 16000;
      healthCheckRetries++;
      
      console.log(`Health check failed, retrying in ${delay}ms (attempt ${healthCheckRetries}/${MAX_RETRIES})`);
      
      return new Promise((resolve) => {
        setTimeout(async () => {
          resolve(await checkHealth());
        }, delay);
      });
    }
    
    return {
      ok: false,
      timestamp: Date.now(),
      retries: healthCheckRetries,
      error: error.message,
    };
  }
};

// ============ SURVEY ENDPOINTS ============

export const getSurveyProgress = async (clientId) => {
  try {
    const clientIdStr = validateClientId(clientId, 'getSurveyProgress');
    const response = await get(`/survey/progress/${clientIdStr}/all`);
    
    if (response.success && response.data) {
      return response.data;
    } else if (response.strategy || response.capabilities || response.readiness) {
      return response;
    } else {
      return {
        strategy: { done: 0, total: 14, status: 'not_started' },
        capabilities: { done: 0, total: 14, status: 'not_started' },
        readiness: { done: 0, total: 14, status: 'not_started' },
      };
    }
  } catch (error) {
    console.error(`Failed to fetch survey progress for client ${clientId}:`, error);
    return {
      strategy: { done: 0, total: 14, status: 'not_started' },
      capabilities: { done: 0, total: 14, status: 'not_started' },
      readiness: { done: 0, total: 14, status: 'not_started' },
      error: true,
      message: error.message,
    };
  }
};

export const submitSurvey = async (surveyType, data) => {
  // Validate and convert client_id in data
  if (data.client_id) {
    data.client_id = validateClientId(data.client_id, 'submitSurvey');
  }
  return post(`/survey/${surveyType}`, data);
};

export const completeSurvey = async (clientId, surveyType) => {
  const clientIdStr = validateClientId(clientId, 'completeSurvey');
  return post('/survey/complete', { client_id: clientIdStr, survey_type: surveyType });
};

// ============ USE CASE QA ENDPOINTS ============

export async function fetchUseCaseList() {
  const r = await fetch(`${API_BASE}/api/v2/useCases/list`, { credentials: "include" });
  if (!r.ok) throw new Error(`List HTTP ${r.status}`);
  const json = await r.json();
  if (!json?.success) throw new Error(json?.message || "List failed");
  return {
    items: json.data || [],
    count: json.count ?? (json.data ? json.data.length : 0),
    timestamp: json.timestamp || new Date().toISOString()
  };
}

export async function fetchUseCaseQA(id) {
  const r = await fetch(`${API_BASE}/api/v2/useCases/${id}/qa`, { credentials: "include" });
  if (!r.ok) throw new Error(`QA HTTP ${r.status}`);
  const json = await r.json();
  if (!json?.success) throw new Error(json?.message || "QA failed");
  return json.data;
}

// ============ USE CASE PROFILE ENDPOINTS (V2) ============

/**
 * Fetch Use Case Detail (primary endpoint)
 * This is the authoritative source for use case data including scores and financial_snapshot
 * @param {string|number} id - Use case ID
 * @param {string|number} clientId - Client ID (REQUIRED - no default)
 */
export const getUseCaseDetail = async (id, clientId) => {
  try {
    const clientIdStr = validateClientId(clientId, 'getUseCaseDetail');
    const response = await get(`/api/v2/useCases/${id}?client_id=${clientIdStr}`);
    return response.data || response;
  } catch (error) {
    console.error('Error fetching use case detail:', error);
    throw error;
  }
};

/**
 * Fetch Use Case Profile - uses v2 detail under the hood
 * @param {string|number} id - Use case ID
 * @param {string|number} clientId - Client ID (REQUIRED - no default)
 */
export const getUseCaseProfile = async (id, clientId) => {
  try {
    const clientIdStr = validateClientId(clientId, 'getUseCaseProfile');
    return await getUseCaseDetail(id, clientIdStr);
  } catch (error) {
    console.error('Error fetching use case profile:', error);
    return null;
  }
};

/**
 * Fetch RAW scores - prefer detail, fall back to dedicated endpoint
 * This ensures we always get the actual stored scores, not calculated values
 * @param {string|number} useCaseId - Use case ID
 * @param {string|number} clientId - Client ID (REQUIRED - no default)
 */
export const getUseCaseScores = async (useCaseId, clientId) => {
  try {
    const clientIdStr = validateClientId(clientId, 'getUseCaseScores');
    
    // First try to get scores from detail endpoint
    const detail = await getUseCaseDetail(useCaseId, clientIdStr);
    if (detail?.total_score != null || detail?.value_score != null) {
      return {
        value_score: detail.value_score ?? null,
        implementation_score: detail.implementation_score ?? null,
        total_score: detail.total_score ?? null,
        rank: detail.rank ?? null,
      };
    }
    
    // Optional: Try dedicated scores route if detail doesn't have scores
    const response = await fetch(
      `${API_BASE}/api/v2/useCases/${useCaseId}/scores?client_id=${clientIdStr}`,
      {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );
    
    if (!response.ok) {
      console.warn(`Scores endpoint not available: ${response.status}`);
      return null;
    }
    
    const data = await response.json();
    return data.data || data;
  } catch (error) {
    console.warn('Scores fallback failed:', error);
    return null;
  }
};

/**
 * Fetch ROI data - map from financial_snapshot if dedicated route missing
 * @param {string|number} useCaseId - Use case ID
 * @param {string|number} clientId - Client ID (REQUIRED - no default)
 */
export const getUseCaseROI = async (useCaseId, clientId) => {
  try {
    const clientIdStr = validateClientId(clientId, 'getUseCaseROI');
    
    // First try to get financial_snapshot from detail endpoint
    const detail = await getUseCaseDetail(useCaseId, clientIdStr);
    if (detail?.financial_snapshot) {
      const f = detail.financial_snapshot;
      return {
        assumptions: [],
        calculations: [],
        sources: [],
        paybackMonths: f.payback_months ?? null,
        confidenceRollup: f.confidence ?? 'Medium',
        annualRevenuePotential: f.annual_revenue_potential ?? null,
        provenance: f.provenance ?? null,
        revenueBandLabel: f.revenue_band_label ?? null,
        explanation: f.explanation ?? null,
      };
    }
    
    // Optional: Try dedicated ROI route if it exists
    try {
      const response = await get(`/api/v2/useCases/${useCaseId}/roi?clientId=${clientIdStr}`);
      if (response.success && response.data) {
        return response.data;
      }
    } catch (err) {
      console.warn('Dedicated ROI endpoint not available:', err);
    }
    
    // Return empty structure if no data
    return {
      assumptions: [],
      calculations: [],
      sources: [],
      paybackMonths: null,
      confidenceRollup: 'Medium'
    };
  } catch (error) {
    console.error('Error fetching ROI data:', error);
    return {
      assumptions: [],
      calculations: [],
      sources: [],
      paybackMonths: null,
      confidenceRollup: 'Medium'
    };
  }
};

/**
 * Fetch Use Case Sections data with client customization support
 * @param {string|number} id - Use case ID
 * @param {string|number} clientId - Client ID (REQUIRED - no default)
 */
export const getUseCaseSections = async (id, clientId) => {
  try {
    const clientIdStr = validateClientId(clientId, 'getUseCaseSections');
    const response = await get(`/api/v2/useCases/${id}/sections?clientId=${clientIdStr}`);
    return response.data || response || [];
  } catch (error) {
    console.error('Error fetching use case sections:', error);
    return [];
  }
};

/**
 * Fetch Use Case Rationalizations data
 */
export const getUseCaseRationalizations = async (id) => {
  try {
    const response = await get(`/api/v2/useCases/${id}/rationalizations`);
    return response.data || response || [];
  } catch (error) {
    console.error('Error fetching use case rationalizations:', error);
    return [];
  }
};

/**
 * Fetch risks data for a use case
 * @param {string|number} useCaseId - Use case ID
 * @param {string|number} clientId - Client ID (REQUIRED - no default)
 */
export const getUseCaseRisks = async (useCaseId, clientId) => {
  try {
    const clientIdStr = validateClientId(clientId, 'getUseCaseRisks');
    const response = await get(`/api/v2/useCases/${useCaseId}/risks?clientId=${clientIdStr}`);
    return response.data || response || [];
  } catch (error) {
    console.error('Error fetching risks:', error);
    return [];
  }
};

/**
 * Fetch alignment data for a use case
 * @param {string|number} useCaseId - Use case ID
 * @param {string|number} clientId - Client ID (REQUIRED - no default)
 */
export const getUseCaseAlignment = async (useCaseId, clientId) => {
  try {
    const clientIdStr = validateClientId(clientId, 'getUseCaseAlignment');
    const response = await get(`/api/v2/useCases/${useCaseId}/alignment?clientId=${clientIdStr}`);
    return response.data || response || [];
  } catch (error) {
    console.error('Error fetching alignment:', error);
    return [];
  }
};

/**
 * Fetch roadmap data for a use case
 * @param {string|number} useCaseId - Use case ID
 * @param {string|number} clientId - Client ID (REQUIRED - no default)
 */
export const getUseCaseRoadmap = async (useCaseId, clientId) => {
  try {
    const clientIdStr = validateClientId(clientId, 'getUseCaseRoadmap');
    const response = await get(`/api/v2/useCases/${useCaseId}/roadmap?clientId=${clientIdStr}`);
    return response.data || response || [];
  } catch (error) {
    console.error('Error fetching roadmap:', error);
    return [];
  }
};

/**
 * Fetch feasibility data for a use case
 * @param {string|number} useCaseId - Use case ID
 * @param {string|number} clientId - Client ID (REQUIRED - no default)
 */
export const getUseCaseFeasibility = async (useCaseId, clientId) => {
  try {
    const clientIdStr = validateClientId(clientId, 'getUseCaseFeasibility');
    const response = await get(`/api/v2/useCases/${useCaseId}/feasibility?clientId=${clientIdStr}`);
    return response.data || response || {};
  } catch (error) {
    console.error('Error fetching feasibility:', error);
    return {};
  }
};

/**
 * Fetch feasibility assessment (alternative naming for consistency)
 * @param {string|number} clientId - Client ID (REQUIRED - no default)
 * @param {string|number} useCaseId - Use case ID
 */
export const getFeasibility = async (clientId, useCaseId) => {
  const clientIdStr = validateClientId(clientId, 'getFeasibility');
  return getUseCaseFeasibility(useCaseId, clientIdStr);
};

/**
 * Fetch roadmap (alternative naming for consistency)
 * @param {string|number} clientId - Client ID (REQUIRED - no default)
 * @param {string|number} useCaseId - Use case ID
 */
export const getRoadmap = async (clientId, useCaseId) => {
  const clientIdStr = validateClientId(clientId, 'getRoadmap');
  return getUseCaseRoadmap(useCaseId, clientIdStr);
};

/**
 * Fetch signed URL for profile artifact download
 */
export const getArtifactUrl = async (id) => {
  try {
    const response = await fetch(`${API_BASE}/api/v2/useCases/${id}/artifact`, {
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });
    
    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      throw new Error(`Failed to fetch artifact URL: ${response.statusText}`);
    }
    
    const data = await response.json();
    return data.data?.url || data.url || null;
  } catch (error) {
    console.error('Error fetching artifact URL:', error);
    return null;
  }
};

// ============ ACTIONS ENDPOINTS ============

/**
 * Fetch all actions for a client, optionally filtered by use case
 * @param {string|number} clientId - Client ID (REQUIRED - no default)
 * @param {string|number|null} useCaseId - Optional use case filter
 */
export const getActions = async (clientId, useCaseId = null) => {
  try {
    const clientIdStr = validateClientId(clientId, 'getActions');
    const queryParams = useCaseId ? `?use_case_id=${useCaseId}` : '';
    const response = await get(`/api/v2/actions/${clientIdStr}${queryParams}`);
    return response.data || response || [];
  } catch (error) {
    console.error('Error fetching actions:', error);
    return [];
  }
};

/**
 * Create a new action
 * @param {object} actionData - Action data (client_id will be validated)
 */
export const createAction = async (actionData) => {
  try {
    // Validate client_id in actionData
    if (actionData.client_id) {
      actionData.client_id = validateClientId(actionData.client_id, 'createAction');
    }
    const response = await post('/api/v2/actions', actionData);
    return response.data || response;
  } catch (error) {
    console.error('Error creating action:', error);
    throw error;
  }
};

/**
 * Update an existing action (e.g., toggle status)
 * @param {string|number} actionId - Action ID
 * @param {object} updates - Update data (client_id will be validated if present)
 */
export const updateAction = async (actionId, updates) => {
  try {
    // Validate client_id in updates if present
    if (updates.client_id) {
      updates.client_id = validateClientId(updates.client_id, 'updateAction');
    }
    const response = await put(`/api/v2/actions/${actionId}`, updates);
    return response.data || response;
  } catch (error) {
    console.error('Error updating action:', error);
    throw error;
  }
};

// ============ STRATEGIC INSIGHTS ============

export const generateStrategicInsights = async (componentName, additionalParams = {}) => {
  try {
    // Validate any client_id in additionalParams
    if (additionalParams.client_id) {
      additionalParams.client_id = validateClientId(additionalParams.client_id, 'generateStrategicInsights');
    }
    
    const response = await fetch(`${BACKEND_URL}/api/generate-strategic-insights`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ componentName, additionalParams }),
    });

    const data = await response.json();

    if (data.success) {
      return { jsx: data.jsx, insights: data.insights };
    } else {
      throw new Error(data.error || "Failed to fetch insights from backend.");
    }
  } catch (error) {
    console.error('API service error:', error);
    throw error;
  }
};

/**
 * Get strategic alignment scores
 * @param {string|number} client_id - Client ID (REQUIRED - no default)
 * @param {string|number} use_case_id - Use case ID
 */
export const getStrategicAlignmentScores = async (client_id, use_case_id) => {
  try {
    const clientIdStr = validateClientId(client_id, 'getStrategicAlignmentScores');
    const response = await fetch(`${BACKEND_URL}/api/get-strategic-alignment-scores`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        client_id: clientIdStr, 
        use_case_id: use_case_id 
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (data.success) {
      return data.scores;
    } else {
      throw new Error(data.error || "Failed to fetch strategic alignment scores from backend.");
    }
  } catch (error) {
    console.error('API service error:', error);
    throw error;
  }
};

// Export API base for debugging
export const getApiBase = () => API_BASE;

// Export retry management for health check UI
export const getHealthCheckStatus = () => ({
  retries: healthCheckRetries,
  maxRetries: MAX_RETRIES,
  isRetrying: healthCheckRetries > 0 && healthCheckRetries < MAX_RETRIES,
});

// Create a default export with all functions
const apiService = {
  get,
  post,
  put,
  checkHealth,
  getRoadmap,
  getFeasibility,
  getSurveyProgress,
  submitSurvey,
  completeSurvey,
  fetchUseCaseList,
  fetchUseCaseQA,
  getUseCaseDetail,
  getUseCaseProfile,
  getUseCaseScores,
  getUseCaseSections,
  getUseCaseRationalizations,
  getUseCaseROI,
  getUseCaseRisks,
  getUseCaseAlignment,
  getUseCaseRoadmap,
  getUseCaseFeasibility,
  getArtifactUrl,
  generateStrategicInsights,
  getStrategicAlignmentScores,
  getApiBase,
  getHealthCheckStatus,
  // Actions methods
  getActions,
  createAction,
  updateAction
};

export default apiService;